import { Link } from '@tanstack/react-router';
import { CircleAlert, Plus, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { moduleDefinition, type AdminOverview, type AdminRecentChange, type ModuleKey } from '@nest-recruit/shared';
import { Avatar } from '../../../components/Avatar';
import { buttonClassName } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Skeleton, SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { formatDate, formatDateTime, formatNumber } from '../../../lib/format';
import { useModules } from '../../../lib/modules';
import { useCanEdit, useCanView, useCurrentSession } from '../../../stores/session';
import { ADMIN_GROUPS, visibleAdminMenu, type AdminSection, type AdminSectionId } from '../admin-sections';
import { useAdminOverview } from '../api/overview-api';
import { AttentionList } from '../components/overview/AttentionList';
import { SECTION_OF_STATUS, describeChange, greeting, lede, sectionStatus } from '../components/overview/overview-words';
import styles from './AdminOverviewPage.module.css';

/**
 * ADMIN-21 Overview, Admin's home (Admin redesign 2026-11-02, Option A of `1-shell/admin-home.html`, the health
 * board): the headline numbers, "Needs your attention" with a fix on every line, the parts switched on, recent
 * changes and "Where things stand". Everything comes from `GET /admin/overview`, counted live (non-negotiable #10).
 *
 * Admin only: HR lands on their first page instead, and anyone the server refuses sees the refusal. There is no
 * "Finish setting up" strip (owner, 2026-11-02).
 */
export function AdminOverviewPage(): ReactNode {
  const canView = useCanView('admin_config');
  const overview = useAdminOverview(canView);

  if (!canView || overview.error?.status === 403) return <ForbiddenState resource="the admin overview" contact="Your admin" />;
  if (overview.data) return <Board overview={overview.data} />;
  if (overview.error) return <ServerErrorState requestId={overview.error.requestId} onRetry={() => void overview.refetch()} />;
  return <BoardSkeleton />;
}

/** The menu's sections by id, for "Where things stand" and its links. */
const SECTIONS: ReadonlyMap<AdminSectionId, AdminSection> = new Map(ADMIN_GROUPS.flatMap((group) => group.sections.map((section) => [section.id, section] as const)));

/** How many parts each list shows before "+ N more", which opens Master settings. */
const PARTS_SHOWN = 8;

function Board({ overview }: { readonly overview: AdminOverview }): ReactNode {
  const session = useCurrentSession();
  const menu = visibleAdminMenu(session, useModules());
  const canInvite = useCanEdit('user_management');
  const openable = new Set(menu.groups.flatMap((group) => group.sections.map((section) => section.id)));
  const { headline } = overview;
  const firstName = session?.fullName.split(' ')[0] ?? '';

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Overview</h1>
          <p className={styles.lede}>{lede(headline.needsYou, firstName, greeting(new Date(), session?.timezone ?? 'Asia/Dhaka'))}</p>
        </div>
        {canInvite && (
          <Link to="/admin/invite" className={buttonClassName('solid')}>
            <Plus size={16} aria-hidden />
            Invite somebody
          </Link>
        )}
      </header>

      <div className={styles.tiles}>
        <a href="#needs-attention" className={styles.tile}>
          <span className={styles.tileLabel}>Needs you</span>
          <span className={styles.tileValue}>{formatNumber(headline.needsYou)}</span>
          <span className={styles.tileSub}>{headline.needsYou === 0 ? 'Nothing waiting' : 'Most urgent first, below'}</span>
        </a>
        <Link to="/admin/master-settings" className={styles.tile}>
          <span className={styles.tileLabel}>Parts switched on</span>
          <span className={styles.tileValue}>
            {formatNumber(headline.partsSwitchedOn.on)} <span className={styles.tileOf}>of {formatNumber(headline.partsSwitchedOn.of)}</span>
          </span>
          <span className={styles.tileSub}>
            {headline.partsSwitchedOn.lastChangeAt === null
              ? 'Never changed'
              : `Last change ${formatDate(headline.partsSwitchedOn.lastChangeAt)} by ${lastChangeBy(headline.partsSwitchedOn.lastChangeByName, session?.fullName)}`}
          </span>
        </Link>
        <Link to="/admin/email" className={styles.tile}>
          <span className={styles.tileLabel}>Emails today</span>
          <span className={styles.tileValue}>{formatNumber(headline.emailsToday.delivered + headline.emailsToday.failed)}</span>
          <span className={styles.tileSub}>
            {headline.emailsToday.failed > 0 ? (
              <span className={styles.failed}>
                <CircleAlert size={14} aria-hidden />
                {formatNumber(headline.emailsToday.failed)} failed
              </span>
            ) : (
              'None failed'
            )}
            {` · ${formatNumber(headline.emailsToday.delivered)} delivered`}
          </span>
        </Link>
        <Link to="/admin/users" className={styles.tile}>
          <span className={styles.tileLabel}>People</span>
          <span className={styles.tileValue}>{formatNumber(headline.people.active)}</span>
          <span className={styles.tileSub}>{`active · ${formatNumber(headline.people.invited)} invited`}</span>
        </Link>
      </div>

      <div className={styles.columns}>
        <section id="needs-attention" className={styles.attention} aria-labelledby="needs-attention-title">
          <header className={styles.cardHead}>
            <h2 id="needs-attention-title" className={styles.cardTitle}>
              Needs your attention
            </h2>
            {overview.attention.length > 0 && <span className={styles.count}>{overview.attention.length}</span>}
            <span className={styles.cardMeta}>Most urgent first</span>
          </header>
          <AttentionList items={overview.attention} />
        </section>

        <div className={styles.side}>
          <Card title="Parts of the app" action={<Link to="/admin/master-settings" className={styles.link}>Master settings</Link>}>
            <Parts on={overview.parts.on} off={overview.parts.off} />
          </Card>
          <Card title="Recent changes" action={<Link to="/admin/events" className={styles.link}>Event log</Link>}>
            <RecentChanges changes={overview.recentChanges} viewerId={session?.id ?? null} />
          </Card>
        </div>
      </div>

      <Card title="Where things stand" meta="Every section, live">
        <ul className={styles.stand}>
          {overview.sections.map((status) => {
            const id = SECTION_OF_STATUS[status.key];
            const section = SECTIONS.get(id);
            const words = sectionStatus(status);
            const label = section?.label ?? id;
            return (
              <li key={status.key} className={styles.standRow}>
                {section && openable.has(id) ? (
                  <Link to={section.to} className={styles.standName}>
                    {label}
                  </Link>
                ) : (
                  <span className={styles.standName}>{label}</span>
                )}
                <span className={words.needsLook ? styles.standLook : styles.standText}>
                  {words.needsLook && <TriangleAlert size={14} aria-hidden className={styles.warn} />}
                  {words.text}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>
    </div>
  );
}

function lastChangeBy(name: string | null, viewerName: string | undefined): string {
  if (name === null) return 'the system';
  return name === viewerName ? 'you' : name;
}

function Parts({ on, off }: { readonly on: readonly ModuleKey[]; readonly off: readonly ModuleKey[] }): ReactNode {
  const shownOff = off.slice(0, PARTS_SHOWN);
  const moreOff = off.length - shownOff.length;
  const shownOn = on.slice(0, PARTS_SHOWN);
  const moreOn = on.length - shownOn.length;
  return (
    <div className={styles.parts}>
      <p className={styles.partsHead}>Switched on · {formatNumber(on.length)}</p>
      {on.length === 0 ? (
        <p className={styles.quiet}>—</p>
      ) : (
        <ul className={styles.onList}>
          {shownOn.map((key) => (
            <li key={key} className={styles.onPart}>
              <span className={styles.dot} aria-hidden />
              {moduleDefinition(key).label}
            </li>
          ))}
          {moreOn > 0 && (
            <li>
              <Link to="/admin/master-settings" className={styles.link}>
                + {moreOn} more
              </Link>
            </li>
          )}
        </ul>
      )}
      <p className={styles.partsHead}>Switched off · {formatNumber(off.length)}</p>
      {off.length === 0 ? (
        <p className={styles.quiet}>—</p>
      ) : (
        <ul className={styles.offList}>
          {shownOff.map((key) => (
            <li key={key} className={styles.offPart}>
              {moduleDefinition(key).label}
            </li>
          ))}
          {moreOff > 0 && (
            <li>
              <Link to="/admin/master-settings" className={styles.offPart}>
                + {moreOff} more
              </Link>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function RecentChanges({ changes, viewerId }: { readonly changes: readonly AdminRecentChange[]; readonly viewerId: string | null }): ReactNode {
  if (changes.length === 0) return <p className={styles.quiet}>No settings have changed yet.</p>;
  return (
    <ul className={styles.changes}>
      {changes.map((change) => {
        const words = describeChange(change, viewerId);
        return (
          <li key={change.id} className={styles.change}>
            <Avatar name={change.actorName ?? 'System'} size="sm" isSystem={change.actorId === null} />
            <span className={styles.changeText}>
              {`${words.actor} ${words.action}`}
              {words.subject !== null && (
                <>
                  {' '}
                  <strong>{words.subject}</strong>
                </>
              )}
              <time className={styles.changeTime} dateTime={change.occurredAt}>
                {formatDateTime(change.occurredAt)}
              </time>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

/** First paint only: shaped like the board, never a spinner (06-qol §5). */
function BoardSkeleton(): ReactNode {
  return (
    <div className={styles.page} aria-busy="true">
      <header className={styles.head}>
        <div className={styles.headText}>
          <h1 className={styles.title}>Overview</h1>
          <Skeleton width="40%" />
        </div>
      </header>
      <div className={styles.tiles}>
        {[0, 1, 2, 3].map((index) => (
          <div key={index} className={styles.tile}>
            <Skeleton width="50%" />
            <Skeleton shape="title" width="35%" />
          </div>
        ))}
      </div>
      <div className={styles.columns}>
        <Card title="Needs your attention">
          <SkeletonLines count={5} />
        </Card>
        <div className={styles.side}>
          <Card title="Parts of the app">
            <SkeletonLines count={3} />
          </Card>
        </div>
      </div>
    </div>
  );
}
